/**
 * Mock service for Business Profile settings endpoints.
 * Simulates backend endpoints that are not yet implemented.
 *
 * When the backend is ready, replace method bodies with real $axios calls
 * and remove localStorage usage + _delay calls.
 */

const STORAGE_KEY = 'bp_settings_mock';

const _load = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { profiles: {}, defaultMarketplaces: {} };
  } catch {
    return { profiles: {}, defaultMarketplaces: {} };
  }
};

let _data = _load();

const _save = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_data));
  } catch (e) {
    console.warn('[businessProfileMockService] Failed to save to localStorage:', e);
  }
};

const _delay = (ms = 200) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Update business profile data (name, etc.)
 * Simulates: PATCH /businessProfiles/{id}
 *
 * @param {number|string} id
 * @param {{ name?: string }} updates
 * @returns {Promise<{ id, name, updatedAt }>}
 */
const updateBusinessProfile = async (id, updates) => {
  await _delay(250);
  const existing = _data.profiles[id] || { id };
  const updated = {
    ...existing,
    ...updates,
    id,
    updatedAt: new Date().toISOString(),
  };
  _data.profiles[id] = updated;
  _save();
  return { ...updated };
};

/**
 * Get the default marketplace for a business profile as { marketplaceUid, profileId }.
 * Simulates: GET /businessProfiles/{bpId}/defaultMarketplace
 *
 * Back-compat: legacy entries stored a scalar profileId; map those to an ads uid.
 *
 * @param {number|string} bpId
 * @returns {Promise<{ marketplaceUid: string|null, profileId: number|null }>}
 */
const getDefaultMarketplace = async (bpId) => {
  await _delay(150);
  const stored = _data.defaultMarketplaces[bpId] ?? null;
  if (stored && typeof stored === 'object') {
    return { marketplaceUid: stored.marketplaceUid ?? null, profileId: stored.profileId ?? null };
  }
  // Legacy scalar profileId.
  return { marketplaceUid: stored != null ? `ads:${stored}` : null, profileId: stored ?? null };
};

/**
 * Set the default marketplace for a business profile.
 * Simulates: PATCH /businessProfiles/{bpId}/defaultMarketplace
 *
 * @param {number|string} bpId
 * @param {{ marketplaceUid: string|null, profileId: number|null }} value
 * @returns {Promise<{ bpId, marketplaceUid, profileId, updatedAt }>}
 */
const setDefaultMarketplace = async (bpId, value) => {
  await _delay(200);
  _data.defaultMarketplaces[bpId] = value;
  _save();
  return { bpId, ...value, updatedAt: new Date().toISOString() };
};

/**
 * Clear the default marketplace for a business profile.
 * @param {number|string} bpId
 */
const clearDefaultMarketplace = async (bpId) => {
  await _delay(150);
  delete _data.defaultMarketplaces[bpId];
  _save();
  return { bpId, profileId: null };
};

const clearAll = () => {
  _data = { profiles: {}, defaultMarketplaces: {} };
  localStorage.removeItem(STORAGE_KEY);
};

const businessProfileMockService = {
  updateBusinessProfile,
  getDefaultMarketplace,
  setDefaultMarketplace,
  clearDefaultMarketplace,
  clearAll,
};

export default businessProfileMockService;
